import PropTypes from "prop-types";
import { Bell, Trash2, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  formatNotificationTime,
  getChannelMeta,
  notificationTitle,
} from "@/lib/inboundNotifications";

function NotificationRow({ notification, onOpen, onClear }) {
  const meta = getChannelMeta(notification.channel);
  const unread = !notification.is_read;

  return (
    <div
      className={cn(
        "group relative rounded-lg border bg-white p-2.5 transition-colors",
        unread ? "border-sky-200 bg-sky-50/40" : "border-slate-200 hover:border-slate-300",
      )}
      data-testid={`notification-row-${notification.id}`}
      data-read={notification.is_read ? "true" : "false"}
    >
      <button
        type="button"
        className="w-full text-left pr-8"
        onClick={() => onOpen(notification)}
      >
        <div className="flex items-start gap-2.5">
          <span className={cn("mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md", meta.iconBg)}>
            <meta.Icon size={14} strokeWidth={1.75} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <div className="text-[12px] font-semibold text-slate-900 truncate">
                {notification.sender_name || "Unknown"}
              </div>
              {unread && (
                <span className="ml-auto shrink-0 text-[10px] font-semibold uppercase tracking-wide text-sky-700">
                  New
                </span>
              )}
            </div>
            <div className="mt-0.5 text-[11px] text-slate-500">{meta.label}</div>
            {notification.preview ? (
              <div className="mt-1 text-[11.5px] text-slate-600 line-clamp-2">{notification.preview}</div>
            ) : (
              <div className="mt-1 text-[11.5px] text-slate-500 line-clamp-1">{notificationTitle(notification)}</div>
            )}
            <div className="mt-1.5 text-[10.5px] text-slate-400">
              {formatNotificationTime(notification.created_at)}
            </div>
          </div>
        </div>
      </button>
      <button
        type="button"
        className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 opacity-0 group-hover:opacity-100 hover:bg-slate-100 hover:text-slate-700 focus:opacity-100"
        aria-label="Clear notification"
        title="Clear"
        data-testid={`notification-clear-${notification.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onClear(notification.id);
        }}
      >
        <X size={13} strokeWidth={1.75} />
      </button>
    </div>
  );
}

NotificationRow.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.string.isRequired,
    channel: PropTypes.string,
    sender_name: PropTypes.string,
    preview: PropTypes.string,
    is_read: PropTypes.bool,
    created_at: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

export function NotificationCenter({
  open,
  onOpenChange,
  notifications,
  unreadCount,
  loading,
  onOpenNotification,
  onClearOne,
  onClearAll,
  onRefresh,
}) {
  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (next) onRefresh?.({ toastNew: false });
      }}
    >
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-10 w-10 sm:h-8 sm:w-8"
          data-testid="briefing-button"
          title="Notifications"
        >
          <Bell size={16} className="text-slate-600" strokeWidth={1.75} />
          {unreadCount > 0 && (
            <span
              className="absolute top-2 right-2 sm:top-1.5 sm:right-1.5 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white"
              data-testid="notification-unread-dot"
            />
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full max-w-full sm:w-[420px] sm:max-w-[420px] p-0">
        <SheetHeader className="px-5 py-4 border-b border-slate-100 pr-12">
          <div className="flex items-center justify-between gap-2">
            <div>
              <SheetTitle className="text-[13px] font-semibold flex items-center gap-1.5">
                <Bell size={13} className="text-slate-700" /> Notifications
              </SheetTitle>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {unreadCount > 0
                  ? `${unreadCount} unread · Gmail, WhatsApp, Telegram`
                  : "Gmail, WhatsApp, Telegram"}
              </div>
            </div>
            {notifications.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-[11.5px] text-slate-600"
                data-testid="notifications-clear-all"
                onClick={() => onClearAll()}
              >
                <Trash2 size={12} className="mr-1" /> Clear all
              </Button>
            )}
          </div>
        </SheetHeader>
        <div className="p-4 space-y-2 overflow-y-auto scrollbar-thin h-[calc(100dvh-72px)]">
          {loading && notifications.length === 0 && (
            <p className="text-[12px] text-slate-500 py-8 text-center">Loading…</p>
          )}
          {!loading && notifications.length === 0 && (
            <div className="py-10 text-center">
              <div className="mx-auto mb-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                <Bell size={16} />
              </div>
              <p className="text-[12.5px] font-medium text-slate-700">You&apos;re all caught up</p>
              <p className="mt-1 text-[11.5px] text-slate-500">
                New inbound messages will appear here instantly.
              </p>
            </div>
          )}
          {notifications.length > 0 && (
            notifications.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                onOpen={onOpenNotification}
                onClear={onClearOne}
              />
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

NotificationCenter.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  notifications: PropTypes.arrayOf(PropTypes.object).isRequired,
  unreadCount: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  onOpenNotification: PropTypes.func.isRequired,
  onClearOne: PropTypes.func.isRequired,
  onClearAll: PropTypes.func.isRequired,
  onRefresh: PropTypes.func,
};
